import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";

import type { FilterType } from "@/components/@types";
import { ReportsViewCard } from "@/app/app/_/views/ReportsView/ReportsViewCard";
import { widgetUsesFlushContent } from "@/app/app/_/views/ReportsView/ReportsView.utils";
import {
  getCardGridStyle,
  gridCardBodyHeight,
  REPORTS_CARD_HEIGHT_PX,
  gridColumnCountForMode,
  REPORTS_GRID_SCROLL_CONTAINER_CLASS,
  reportsLayoutKeyForMode,
  resolveReportsDashboardVisibleCards,
} from "@/app/app/_/views/ReportsView/reportsGridLayout";
import { useReportsGridMode } from "@/app/app/_/views/ReportsView/ReportsView.hooks";
import { SharedReportLogo } from "@/app/share/report/SharedReportLogo";
import { SharedReportSidebar } from "@/app/share/report/SharedReportSidebar";
import {
  SharedReportTitleRow,
  SharedReportTopbar,
} from "@/app/share/report/SharedReportTopbar";
import { Values } from "@/constants";
import { useDevice } from "@/hooks/useDevice";
import { SharedWidgetBody } from "@/app/share/report/SharedWidgetBody";
import { SharedWidgetFilters } from "@/app/share/report/SharedWidgetFilters";
import {
  buildSharedWidgetFilterSet,
  editableSharedWidgetLocalFilterDefinitions,
  hasEditableSharedGlobalFilters,
  initialSharedGlobalFilters,
  resolveSharedWidgetHeaderFilters,
  mergeEditableSharedFilterChanges,
  widgetSupportsLocalFilters,
} from "@/app/share/report/sharedReportFilters";
import type { SharedViewLayout } from "@/app/share/report/sharedReportTypes";
import {
  buildSharedWidgetLookup,
  resolveSharedApiWidget,
  resolveSharedWidgetIdentifier,
} from "@/app/share/report/sharedReportWidgetLookup";
import { sharedReportWidgetDisplayTitle } from "@/app/share/report/sharedReportWidgetTitle";
import {
  definitionForActiveReportGroup,
  getReportGroups,
  reportLayoutWidgetToSharedShell,
  resolveActiveReportGroupId,
  sharedReportToDefinition,
} from "@/app/share/report/sharedReportToDefinition";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

export function SharedReportView({
  layout,
  title,
}: {
  layout: SharedViewLayout;
  title: string;
}) {
  const definition = useMemo(() => sharedReportToDefinition(layout), [layout]);
  const hasDefinitionWidgets = definition.widgets.length > 0;
  const groups = useMemo(() => getReportGroups(definition), [definition]);
  const [globalFilters, setGlobalFilters] = useState<FilterType[]>(() =>
    initialSharedGlobalFilters(layout),
  );
  const [widgetFilterOverrides, setWidgetFilterOverrides] = useState<
    Record<string, FilterType[]>
  >({});
  const [activeGroupId, setActiveGroupId] = useState(
    () => groups[0]?.id ?? "default",
  );
  const { isNarrow } = useDevice();
  const [gridEl, setGridEl] = useState<HTMLDivElement | null>(null);
  const showGroupNav = groups.length > 1;
  const gridMode = useReportsGridMode(gridEl);
  const layoutKey = reportsLayoutKeyForMode(gridMode);
  const layoutColumns = gridColumnCountForMode(gridMode);
  const dashboardCardHeightPx = REPORTS_CARD_HEIGHT_PX;
  const gridGapPx = Number.parseFloat("16px");

  const selectedGroupId = resolveActiveReportGroupId(definition, activeGroupId);
  const activeGroup = groups.find((group) => group.id === selectedGroupId);
  const headerTitle =
    groups.length > 1 && activeGroup ? `${title} > ${activeGroup.name}` : title;

  useEffect(() => {
    if (!groups.some((group) => group.id === activeGroupId)) {
      setActiveGroupId(groups[0]?.id ?? "default");
    }
  }, [activeGroupId, groups]);

  const tabDefinition = useMemo(
    () => definitionForActiveReportGroup(definition, selectedGroupId),
    [definition, selectedGroupId],
  );

  const apiWidgetLookup = useMemo(
    () =>
      buildSharedWidgetLookup(
        tabDefinition.widgets.map((widget) =>
          reportLayoutWidgetToSharedShell(widget),
        ),
      ),
    [tabDefinition.widgets],
  );

  const tabs = useMemo(
    () => groups.map((group) => ({ id: group.id, label: group.name })),
    [groups],
  );

  const visibleCards = useMemo(() => {
    const currentLayout = tabDefinition.layouts[layoutKey];
    const cards = currentLayout?.cards ?? [];
    return resolveReportsDashboardVisibleCards(
      cards,
      tabDefinition.widgets,
      gridMode,
    );
  }, [gridMode, layoutKey, tabDefinition.layouts, tabDefinition.widgets]);

  const updateWidgetFilterOverrides = useCallback(
    (identifier: string, filters: FilterType[]) => {
      setWidgetFilterOverrides((current) => ({
        ...current,
        [identifier]: filters,
      }));
    },
    [],
  );

  const handleGlobalFiltersChange = useCallback(
    (nextEditable: FilterType[]) => {
      setGlobalFilters((current) =>
        mergeEditableSharedFilterChanges(current, nextEditable),
      );
    },
    [],
  );

  const showFilters =
    hasDefinitionWidgets && hasEditableSharedGlobalFilters(layout);
  const showNarrowHeaderRow = isNarrow && showFilters;

  return (
    <div
      // The logo over the rail beside the title, and the rail beside the
      // widgets - a phone's, one over the other: the logo, the filters where
      // there are any, the widgets.
      className={cn(
        "grid w-full h-full min-h-0 overflow-hidden flex-1 bg-background",
        !isNarrow
          ? "grid-rows-[auto_1fr] [grid-template-areas:'logo_header'_'sidebar_widgets']"
          : showNarrowHeaderRow
            ? "grid-rows-[auto_auto_1fr] [grid-template-areas:'logo'_'header'_'widgets']"
            : "grid-rows-[auto_1fr] [grid-template-areas:'logo'_'widgets']",
      )}
      style={{
        gridTemplateColumns: isNarrow ? "1fr" : `${Values.SIDEBAR_WIDTH}px 1fr`,
      }}
    >
      <div
        className={cn(
          "[grid-area:logo] items-center gap-3 min-h-12 min-w-0 [border-bottom-width:1px] [border-bottom-style:solid] md:[border-right-width:1px] md:[border-right-style:solid] border-border bg-background",
          isNarrow ? "flex pl-3 pr-3" : "block pl-0 pr-0",
        )}
      >
        <SharedReportLogo embedded={isNarrow} />
        {isNarrow ? (
          <SharedReportTitleRow
            title={headerTitle}
            groups={tabs}
            selectedGroupId={selectedGroupId}
            onGroupChange={setActiveGroupId}
            showGroupSelect={showGroupNav}
            compact
          />
        ) : null}
      </div>

      {(!isNarrow || showNarrowHeaderRow) && (
        <div className="[grid-area:header] [border-bottom-width:1px] [border-bottom-style:solid] border-border min-w-0">
          <div className="w-full max-w-360 ml-auto mr-auto pl-3 pr-3 md:pl-4 md:pr-4">
            <SharedReportTopbar
              title={headerTitle}
              layout={layout}
              groups={tabs}
              selectedGroupId={selectedGroupId}
              onGroupChange={setActiveGroupId}
              filters={globalFilters}
              onFiltersChange={handleGlobalFiltersChange}
              showTitle={!isNarrow}
              showGroupSelect={false}
              showFilters={showFilters}
            />
          </div>
        </div>
      )}

      {!isNarrow && (
        <div className="[grid-area:sidebar] [border-right-width:1px] [border-right-style:solid] border-border bg-background min-h-0 overflow-hidden">
          {showGroupNav ? (
            <SharedReportSidebar
              groups={tabs}
              selectedGroupId={selectedGroupId}
              onGroupChange={setActiveGroupId}
            />
          ) : null}
        </div>
      )}

      <div
        className={cn(
          "[grid-area:widgets] min-h-0 min-w-0 overflow-y-auto overflow-x-hidden",
          REPORTS_GRID_SCROLL_CONTAINER_CLASS,
          "pl-3 pr-3 md:pl-4 md:pr-4",
        )}
      >
        <div className="w-full max-w-360 ml-auto mr-auto min-w-0">
          {!hasDefinitionWidgets ? (
            <div className="pt-8 pb-8">
              <Typography level="body-sm" className="text-muted-foreground/80">
                This report has no widgets yet. Open the report in Neoledger and
                save the layout while public sharing is enabled.
              </Typography>
            </div>
          ) : (
            <div
              ref={setGridEl}
              className="grid grid-cols-(--grid-template-columns-2) auto-rows-[400px] gap-4 w-full items-stretch min-w-0"
              style={
                {
                  "--grid-template-columns-2": `repeat(${layoutColumns}, minmax(0, 1fr))`,
                } as CSSProperties
              }
            >
              {visibleCards.length === 0 ? (
                <div className="col-span-full pt-8 pb-8">
                  <Typography
                    level="body-sm"
                    className="text-muted-foreground/80"
                  >
                    No widgets in this tab.
                  </Typography>
                </div>
              ) : (
                visibleCards.map((card) => {
                  const reportLayoutWidget = tabDefinition.widgets.find(
                    (entry) => entry.id === card.cardId,
                  );
                  const apiWidget = resolveSharedApiWidget(
                    card.cardId,
                    reportLayoutWidget,
                    apiWidgetLookup,
                  );
                  const title = sharedReportWidgetDisplayTitle(
                    apiWidget,
                    reportLayoutWidget,
                    card.cardId,
                  );
                  const identifier = apiWidget
                    ? resolveSharedWidgetIdentifier(apiWidget, card.cardId)
                    : "";
                  const showWidgetFilters =
                    apiWidget != null &&
                    widgetSupportsLocalFilters(apiWidget) &&
                    Object.keys(
                      editableSharedWidgetLocalFilterDefinitions(apiWidget),
                    ).length > 0;
                  const widgetLocalFilters =
                    apiWidget && identifier
                      ? (widgetFilterOverrides[identifier] ??
                        resolveSharedWidgetHeaderFilters(
                          apiWidget,
                          reportLayoutWidget,
                        ))
                      : [];
                  const filterSet =
                    apiWidget && identifier
                      ? buildSharedWidgetFilterSet(
                          apiWidget,
                          globalFilters,
                          layout,
                          widgetLocalFilters,
                        )
                      : undefined;
                  const contentFlush = Boolean(
                    reportLayoutWidget &&
                    widgetUsesFlushContent(reportLayoutWidget),
                  );

                  const cardGridStyle = getCardGridStyle(card, gridMode);

                  return (
                    <div
                      key={card.cardId}
                      className="min-w-0 flex flex-col"
                      style={cardGridStyle}
                    >
                      <ReportsViewCard
                        title={title}
                        height={gridCardBodyHeight(
                          card,
                          gridMode,
                          dashboardCardHeightPx,
                          gridGapPx,
                        )}
                        contentFlush={contentFlush}
                        headerFiltersNode={
                          showWidgetFilters && apiWidget ? (
                            <SharedWidgetFilters
                              widget={apiWidget}
                              filters={widgetLocalFilters}
                              onChange={(nextEditable) =>
                                updateWidgetFilterOverrides(
                                  identifier,
                                  mergeEditableSharedFilterChanges(
                                    widgetLocalFilters,
                                    nextEditable,
                                  ),
                                )
                              }
                            />
                          ) : null
                        }
                      >
                        {apiWidget && identifier ? (
                          <SharedWidgetBody
                            widget={apiWidget}
                            identifier={identifier}
                            filterSet={filterSet}
                            reportLayoutWidget={reportLayoutWidget}
                          />
                        ) : apiWidget ? (
                          <Typography
                            level="body-sm"
                            className="text-muted-foreground/80"
                          >
                            ReportsViewWidget unavailable.
                          </Typography>
                        ) : (
                          <Typography
                            level="body-sm"
                            className="text-muted-foreground/80"
                          >
                            ReportsViewWidget unavailable.
                          </Typography>
                        )}
                      </ReportsViewCard>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
